import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../api/client'
import { i18nGlobal } from '../i18n'
import { useNotificationStore } from './notifications'

/**
 * De documentatiesites van dit account (SAAS_PLAN.md §15).
 *
 * Welke hub actief is, staat in het webadres — elke hub heeft er een eigen.
 * Er is dus niets te onthouden in de browser en niets mee te sturen: wisselen
 * is navigeren, en wat je ziet volgt uit waar je bent.
 *
 * Tot 14 september 2026 lag die keuze wél in localStorage en ging hij als
 * header mee. Twee browsers konden daardoor bij dezelfde URL iets anders
 * tonen, en een link naar een beheerpagina was niet te delen.
 */
export interface Hub {
  id: number
  name: string
  slug: string
  /** Het webadres van deze hub. Komt van de server, want het sjabloon
   *  verschilt per omgeving. */
  url: string
  isPrimary: boolean
  sectionsCount: number
  pagesCount: number
  trashed: boolean
  purgeAt: string | null
}


export const useHubStore = defineStore('hubs', () => {
  const hubs = ref<Hub[]>([])
  const currentHubId = ref<number | null>(null)
  const isOwner = ref(false)
  const limit = ref<number | null>(1)
  const inUse = ref(0)
  const canCreate = ref(false)
  const trashDays = ref(30)
  const isLoading = ref(false)

  const actief = computed(() => hubs.value.find((h) => h.id === currentHubId.value) ?? null)
  const zichtbaar = computed(() => hubs.value.filter((h) => !h.trashed))
  const prullenbak = computed(() => hubs.value.filter((h) => h.trashed))

  /** Meer dan één hub? Pas dan is een wisselaar zinvol. */
  const heeftMeerdereHubs = computed(() => zichtbaar.value.length > 1)

  async function fetchHubs() {
    isLoading.value = true

    try {
      const { data } = await api.get('/account/hubs')
      hubs.value = data.hubs
      currentHubId.value = data.currentHubId
      isOwner.value = data.isOwner
      limit.value = data.limits.limit
      inUse.value = data.limits.inUse
      canCreate.value = data.limits.canCreate
      trashDays.value = data.trashDays

    } finally {
      isLoading.value = false
    }
  }

  /**
   * Naar een andere hub: navigeren, niet onthouden (SAAS_PLAN.md §15).
   *
   * Vroeger sloeg dit de keuze op in de browser en herlaadde het de pagina,
   * waardoor het adres bleef staan waar het stond. Gevolg: twee browsers
   * toonden bij dezelfde URL iets anders, en een link naar een beheerpagina
   * was niet te delen — de ontvanger zag zijn eigen onthouden hub.
   *
   * Nu heeft elke hub een eigen adres en is wisselen gewoon navigeren. Het
   * `activate`-verzoek blijft: de server onthoudt de keuze voor de plekken
   * waar de host géén hub aanwijst (het platformdomein, lokale ontwikkeling
   * op `localhost`).
   */
  /**
   * Een eenmalige code om je login mee te nemen naar een ander hubadres.
   *
   * Los van switchTo, want het verwijderen van de hub waar je op staat heeft
   * hem ook nodig — en dan is de vololgorde kritiek: zodra die hub weg is,
   * antwoordt dit adres nergens meer op.
   */
  async function overdrachtscode(): Promise<string | null> {
    try {
      const { data } = await api.post('/auth/handoff-code')

      return data?.code ?? null
    } catch {
      return null
    }
  }

  /** Het adres waar je met die code aankomt. */
  function handoffUrl(basis: string, code: string): string {
    return `${basis.replace(/\/$/, '')}/auth/handoff?code=${encodeURIComponent(code)}`
  }

  async function switchTo(hubId: number) {
    const doel = hubs.value.find((hub) => hub.id === hubId)

    if (!doel) {
      return
    }

    await api.post(`/account/hubs/${hubId}/activate`)

    // Het inlogtoken staat in localStorage, en dat hoort bij één adres — niet
    // bij een domein. Zonder deze stap kom je uitgelogd aan op de andere hub.
    //
    // Daarom een eenmalige hand-off code, hetzelfde mechanisme dat de centrale
    // login al gebruikt om je op een omgevingsadres binnen te laten. Bij
    // aankomst wordt alsnog gecontroleerd of je lid bent, dus dit geeft geen
    // toegang die er niet al was.
    //
    // Lukt het uitgeven niet, dan blijven we staan en zeggen we het.
    //
    // Hier stond eerst een lege catch met "navigeer toch maar": beter een
    // adres dat bestaat dan een knop die niets doet. Dat klopte niet. Je
    // kwam dan uitgelogd aan op de andere hub zónder uitleg, en dat is
    // precies het beeld dat op 16 september 2026 een zoektocht opleverde
    // naar een uitrol die allang goed stond. Een melding tonen en dan
    // meteen wegnavigeren helpt evenmin: die melding verdwijnt met de
    // pagina waar hij op staat.
    //
    // Blijven staan is eerlijker. Je bent hier nog gewoon ingelogd, de
    // wissel is niet gelukt, en dat staat er dan ook. Het activeren
    // hierboven is geen probleem: sinds fase 2 bepaalt het adres welke hub
    // je ziet, dus wat er op het scherm staat blijft kloppen.
    const code = await overdrachtscode()

    if (code === null) {
      useNotificationStore().error(
        i18nGlobal.t('account.hubs.switchFailedTitle'),
        i18nGlobal.t('account.hubs.switchFailedBody')
      )

      return
    }

    window.location.assign(handoffUrl(doel.url, code))
  }

  /**
   * Is dit adres nog vrij? (SAAS_PLAN.md §15d, fase 4)
   *
   * Een hubnaam is een webadres en dus wereldwijd uniek — ook de naam van een
   * klant die je nooit zult zien. Zonder deze controle merk je dat pas als je
   * op opslaan drukt, met een foutmelding over een naam die je net bedacht
   * had. Het antwoord zegt alleen óf hij vrij is, nooit van wie hij is.
   */
  async function checkBeschikbaar(
    slug: string,
    negeerHubId?: number
  ): Promise<{ available: boolean; reason: string | null }> {
    const { data } = await api.get('/account/hubs/available', {
      params: { slug, ignore: negeerHubId },
    })

    return { available: Boolean(data.available), reason: data.reason ?? null }
  }

  async function create(payload: { name: string; slug: string }) {
    const { data } = await api.post('/account/hubs', payload)
    await fetchHubs()
    return data
  }

  async function update(hubId: number, payload: { name: string; slug: string }) {
    const { data } = await api.put(`/account/hubs/${hubId}`, payload)
    await fetchHubs()
    return data
  }

  async function makePrimary(hubId: number) {
    const { data } = await api.post(`/account/hubs/${hubId}/primary`)
    await fetchHubs()
    return data
  }

  /**
   * Een hub naar de prullenbak.
   *
   * Gooi je de hub weg waar je zélf op staat, dan houdt dit adres op te
   * bestaan: elk volgend verzoek hierheen komt uit bij "deze omgeving bestaat
   * niet", en dat is ook wat je in beeld kreeg. De server zegt daarom in zijn
   * antwoord of je hier stond en waar de hoofdhub nu woont; wij nemen je
   * login mee en sturen je daarheen.
   *
   * De code wordt vóór het verwijderen opgehaald. Daarna kan het niet meer -
   * het uitgeven loopt langs ditzelfde adres.
   */
  async function remove(hubId: number) {
    const vertrekken = hubId === currentHubId.value
    const code = vertrekken ? await overdrachtscode() : null

    const { data } = await api.delete(`/account/hubs/${hubId}`)

    if (vertrekken && data?.hoofdhubUrl) {
      window.location.assign(code === null ? data.hoofdhubUrl : handoffUrl(data.hoofdhubUrl, code))

      return data
    }

    await fetchHubs()

    return data
  }

  async function restore(hubId: number) {
    const { data } = await api.post(`/account/hubs/${hubId}/restore`)
    await fetchHubs()
    return data
  }

  return {
    hubs,
    currentHubId,
    isOwner,
    limit,
    inUse,
    canCreate,
    trashDays,
    isLoading,
    actief,
    zichtbaar,
    prullenbak,
    heeftMeerdereHubs,
    fetchHubs,
    switchTo,
    overdrachtscode,
    checkBeschikbaar,
    create,
    update,
    makePrimary,
    remove,
    restore,
  }
})
